import React, { useState, useEffect } from 'react';
import { attendanceAPI } from '../services/api';
import { format, parseISO } from 'date-fns';
import { ChevronDownIcon, ChevronRightIcon, PencilIcon, PlusIcon, CalendarIcon, UserIcon, BriefcaseIcon } from '@heroicons/react/24/outline';

const AttendanceManagement = () => {
  const [groupedData, setGroupedData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsedEmployees, setCollapsedEmployees] = useState(new Set());
  const [collapsedMonths, setCollapsedMonths] = useState(new Set());
  const [collapsedDates, setCollapsedDates] = useState(new Set());
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newRecord, setNewRecord] = useState({
    employee_id: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    check_in: '',
    check_out: ''
  });
  const [employees, setEmployees] = useState([]);
  const [userPermissions, setUserPermissions] = useState(null);

  useEffect(() => {
    fetchUserPermissions();
    fetchAttendanceRecords();
    fetchEmployees();
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUserPermissions = async () => {
    try {
      const response = await attendanceAPI.getUserPermissions();
      console.log('User permissions response:', response.data);
      setUserPermissions(response.data);
    } catch (error) {
      console.error('Error fetching user permissions:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await attendanceAPI.getEmployees();
      setEmployees(response.data.employees || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const startCellEdit = (cellId, currentValue) => {
    setEditingCell(cellId);
    // Convert HH:MM:SS to HH:MM for the time input
    const timeValue = currentValue && currentValue.includes(':') 
      ? currentValue.split(':').slice(0, 2).join(':') 
      : currentValue;
    setEditingValue(timeValue || '');
  };

  const fetchAttendanceRecords = async () => {
    try {
      setLoading(true);
      // Get data for the last 3 months
      const endDate = new Date();
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 3);
      
      let response;
      try {
        response = await attendanceAPI.getTeamAttendanceRange({
          start_date: format(startDate, 'yyyy-MM-dd'),
          end_date: format(endDate, 'yyyy-MM-dd'),
          search: searchTerm
        });
      } catch (error) {
        // Fallback to regular team attendance for today if range endpoint fails
        console.warn('Range endpoint failed, falling back to today:', error.message);
        response = await attendanceAPI.getTeamAttendance({
          date: format(new Date(), 'yyyy-MM-dd'),
          search: searchTerm
        });
      }

      // Transform and group the data
      const transformedData = response.data.attendance.reduce((acc, record) => {
        if (!record.events || record.events.length === 0) {
          return [...acc, {
            id: `${record.employee_id}-${record.date || 'no-date'}`,
            employee_id: record.employee_id || record.employee_db_id,
            employee_name: `${record.first_name} ${record.last_name}`,
            employee_number: record.employee_number,
            date: record.date || format(new Date(), 'yyyy-MM-dd'),
            check_in: record.clock_in || '',
            check_out: record.clock_out || '',
            worked_hours: '0:00',
            extra_hours: '0:00',
            comments: record.comments || '',
            first_name: record.first_name,
            last_name: record.last_name,
            department: record.department,
            job_title: record.job_title,
            email: record.email
          }];
        }

        // Group events by pairs (clock-in/clock-out)
        let currentPair = {};
        const pairs = [];
        record.events.forEach(event => {
          if (event.event_type === 'clock_in') {
            currentPair = {
              check_in: event.event_time,
              check_in_comments: event.comments || ''
            };
          } else if (event.event_type === 'clock_out' && currentPair.check_in) {
            currentPair.check_out = event.event_time;
            currentPair.check_out_comments = event.comments || '';
            pairs.push(currentPair);
            currentPair = {};
          }
        });

        // If no pairs but have single events, add them
        if (pairs.length === 0 && record.events.length > 0) {
          pairs.push({
            check_in: record.events.find(e => e.event_type === 'clock_in')?.event_time || '',
            check_out: record.events.find(e => e.event_type === 'clock_out')?.event_time || '',
            check_in_comments: record.events.find(e => e.event_type === 'clock_in')?.comments || '',
            check_out_comments: record.events.find(e => e.event_type === 'clock_out')?.comments || ''
          });
        }

        return [...acc, ...pairs.map((pair, index) => {
          // Calculate worked hours
          let workedHours = '0:00';
          let extraHours = '0:00';
          
          if (pair.check_in && pair.check_out) {
            const [inHour, inMin] = pair.check_in.split(':').map(Number);
            const [outHour, outMin] = pair.check_out.split(':').map(Number);
            const totalMinutes = (outHour * 60 + outMin) - (inHour * 60 + inMin);
            const wHours = Math.floor(totalMinutes / 60);
            const wMinutes = totalMinutes % 60;
            const extraMinutes = Math.max(0, totalMinutes - (8 * 60));
            const eHours = Math.floor(extraMinutes / 60);
            const eMins = extraMinutes % 60;
            
            workedHours = `${wHours}:${wMinutes.toString().padStart(2, '0')}`;
            extraHours = `${eHours}:${eMins.toString().padStart(2, '0')}`;
          }

          return {
            id: `${record.employee_id}-${record.date}-${index}`,
            employee_id: record.employee_id || record.employee_db_id,
            employee_name: `${record.first_name} ${record.last_name}`,
            employee_number: record.employee_number,
            date: record.date || format(new Date(), 'yyyy-MM-dd'),
            check_in: pair.check_in,
            check_out: pair.check_out,
            worked_hours: workedHours,
            extra_hours: extraHours,
            comments: [pair.check_in_comments, pair.check_out_comments].filter(Boolean).join(' | '),
            first_name: record.first_name,
            last_name: record.last_name,
            department: record.department,
            job_title: record.job_title,
            email: record.email
          };
        })];
      }, []);

      const grouped = groupDataByEmployee(transformedData);
      setGroupedData(grouped);
      setError(null);
    } catch (error) {
      console.error('Error fetching attendance records:', error);
      setError('Failed to load attendance records');
    } finally {
      setLoading(false);
    }
  };

  const groupDataByEmployee = (data, preserveStates = false) => {
    const grouped = data.reduce((acc, record) => {
      const employeeKey = `${record.employee_id}`;
      const employeeName = `${record.first_name} ${record.last_name}`;
      const employeeInfo = {
        name: employeeName,
        employee_number: record.employee_number,
        department: record.department,
        job_title: record.job_title,
        email: record.email
      };
      
      const date = parseISO(record.date || new Date().toISOString().split('T')[0]);
      const monthKey = format(date, 'yyyy-MM');
      const monthLabel = format(date, 'MMMM yyyy');
      const dateKey = record.date || format(new Date(), 'yyyy-MM-dd');
      const dateLabel = format(date, 'EEEE, MMMM dd, yyyy');
      
      // Initialize employee group
      if (!acc[employeeKey]) {
        acc[employeeKey] = {
          info: employeeInfo,
          months: {}
        };
      }
      
      // Initialize month group
      if (!acc[employeeKey].months[monthKey]) {
        acc[employeeKey].months[monthKey] = {
          label: monthLabel,
          dates: {}
        };
      }
      
      // Initialize date group
      if (!acc[employeeKey].months[monthKey].dates[dateKey]) {
        acc[employeeKey].months[monthKey].dates[dateKey] = {
          label: dateLabel,
          records: []
        };
      }
      
      // Add record to appropriate group
      acc[employeeKey].months[monthKey].dates[dateKey].records.push(record);
      
      return acc;
    }, {});
    
    // Set collapsed states - preserve existing if requested, otherwise collapse all
    if (!preserveStates) {
      const employeeKeys = Object.keys(grouped);
      const monthKeys = [];
      const dateKeys = [];
      
      employeeKeys.forEach(empKey => {
        Object.keys(grouped[empKey].months).forEach(monthKey => {
          monthKeys.push(`${empKey}-${monthKey}`);
          Object.keys(grouped[empKey].months[monthKey].dates).forEach(dateKey => {
            dateKeys.push(`${empKey}-${monthKey}-${dateKey}`);
          });
        });
      });
      
      setCollapsedEmployees(new Set(employeeKeys));
      setCollapsedMonths(new Set(monthKeys));
      setCollapsedDates(new Set(dateKeys));
    }
    
    return grouped;
  };

  const refreshAttendanceData = async () => {
    try {
      setLoading(true);
      // Get data for the last 3 months
      const endDate = new Date();
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 3);
      
      let response;
      try {
        response = await attendanceAPI.getTeamAttendanceRange({
          start_date: format(startDate, 'yyyy-MM-dd'),
          end_date: format(endDate, 'yyyy-MM-dd'),
          search: searchTerm
        });
      } catch (error) {
        // Fallback to regular team attendance for today if range endpoint fails
        console.warn('Range endpoint failed, falling back to today:', error.message);
        response = await attendanceAPI.getTeamAttendance({
          date: format(new Date(), 'yyyy-MM-dd'),
          search: searchTerm
        });
      }

      // Transform and group the data (same logic as fetchAttendanceRecords)
      const transformedData = response.data.attendance.reduce((acc, record) => {
        if (!record.events || record.events.length === 0) {
          return [...acc, {
            id: `${record.employee_id}-${record.date || 'no-date'}`,
            employee_id: record.employee_id || record.employee_db_id,
            employee_name: `${record.first_name} ${record.last_name}`,
            employee_number: record.employee_number,
            date: record.date || format(new Date(), 'yyyy-MM-dd'),
            check_in: record.clock_in || '',
            check_out: record.clock_out || '',
            worked_hours: '0:00',
            extra_hours: '0:00',
            comments: record.comments || '',
            first_name: record.first_name,
            last_name: record.last_name,
            department: record.department,
            job_title: record.job_title,
            email: record.email
          }];
        }

        // Group events by pairs (clock-in/clock-out)
        let currentPair = {};
        const pairs = [];
        record.events.forEach(event => {
          if (event.event_type === 'clock_in') {
            currentPair = {
              check_in: event.event_time,
              check_in_comments: event.comments || ''
            };
          } else if (event.event_type === 'clock_out' && currentPair.check_in) {
            currentPair.check_out = event.event_time;
            currentPair.check_out_comments = event.comments || '';
            pairs.push(currentPair);
            currentPair = {};
          }
        });

        // If no pairs but have single events, add them
        if (pairs.length === 0 && record.events.length > 0) {
          pairs.push({
            check_in: record.events.find(e => e.event_type === 'clock_in')?.event_time || '',
            check_out: record.events.find(e => e.event_type === 'clock_out')?.event_time || '',
            check_in_comments: record.events.find(e => e.event_type === 'clock_in')?.comments || '',
            check_out_comments: record.events.find(e => e.event_type === 'clock_out')?.comments || ''
          });
        }

        return [...acc, ...pairs.map((pair, index) => {
          // Calculate worked hours
          let workedHours = '0:00';
          let extraHours = '0:00';
          
          if (pair.check_in && pair.check_out) {
            const [inHour, inMin] = pair.check_in.split(':').map(Number);
            const [outHour, outMin] = pair.check_out.split(':').map(Number);
            const totalMinutes = (outHour * 60 + outMin) - (inHour * 60 + inMin);
            const wHours = Math.floor(totalMinutes / 60);
            const wMinutes = totalMinutes % 60;
            const extraMinutes = Math.max(0, totalMinutes - (8 * 60));
            const eHours = Math.floor(extraMinutes / 60);
            const eMins = extraMinutes % 60;
            
            workedHours = `${wHours}:${wMinutes.toString().padStart(2, '0')}`;
            extraHours = `${eHours}:${eMins.toString().padStart(2, '0')}`;
          }

          return {
            id: `${record.employee_id}-${record.date}-${index}`,
            employee_id: record.employee_id || record.employee_db_id,
            employee_name: `${record.first_name} ${record.last_name}`,
            employee_number: record.employee_number,
            date: record.date || format(new Date(), 'yyyy-MM-dd'),
            check_in: pair.check_in,
            check_out: pair.check_out,
            worked_hours: workedHours,
            extra_hours: extraHours,
            comments: [pair.check_in_comments, pair.check_out_comments].filter(Boolean).join(' | '),
            first_name: record.first_name,
            last_name: record.last_name,
            department: record.department,
            job_title: record.job_title,
            email: record.email
          };
        })];
      }, []);

      const grouped = groupDataByEmployee(transformedData, true); // Preserve collapsed states
      setGroupedData(grouped);
      setError(null);
    } catch (error) {
      console.error('Error refreshing attendance data:', error);
      setError('Failed to refresh attendance data');
    } finally {
      setLoading(false);
    }
  };

  const handleCellEdit = async (record, field, newValue) => {
    // Store original data for potential rollback
    const originalGroupedData = JSON.parse(JSON.stringify(groupedData));
    
    try {
      if (!newValue || newValue === record[field]) {
        setEditingCell(null);
        setEditingValue('');
        return;
      }
      
      // Optimistic update - update UI immediately with proper deep copy
      const updatedGroupedData = JSON.parse(JSON.stringify(groupedData));
      
      // Find and update the record in the grouped data
      Object.keys(updatedGroupedData).forEach(employeeKey => {
        Object.keys(updatedGroupedData[employeeKey].months).forEach(monthKey => {
          Object.keys(updatedGroupedData[employeeKey].months[monthKey].dates).forEach(dateKey => {
            const dateRecords = updatedGroupedData[employeeKey].months[monthKey].dates[dateKey].records;
            const recordIndex = dateRecords.findIndex(r => r.id === record.id);
            if (recordIndex !== -1) {
              updatedGroupedData[employeeKey].months[monthKey].dates[dateKey].records[recordIndex] = {
                ...dateRecords[recordIndex],
                [field]: field === 'comments' ? newValue : (field === 'check_in' || field === 'check_out' ? (newValue.includes(':') && newValue.split(':').length === 2 ? newValue + ':00' : newValue) : newValue)
              };
              
              // Recalculate worked hours if check_in or check_out changed
              if (field === 'check_in' || field === 'check_out') {
                const updatedRecord = updatedGroupedData[employeeKey].months[monthKey].dates[dateKey].records[recordIndex];
                if (updatedRecord.check_in && updatedRecord.check_out) {
                  const [inHour, inMin] = updatedRecord.check_in.split(':').map(Number);
                  const [outHour, outMin] = updatedRecord.check_out.split(':').map(Number);
                  const totalMinutes = (outHour * 60 + outMin) - (inHour * 60 + inMin);
                  const wHours = Math.floor(totalMinutes / 60);
                  const wMinutes = totalMinutes % 60;
                  const extraMinutes = Math.max(0, totalMinutes - (8 * 60));
                  const eHours = Math.floor(extraMinutes / 60);
                  const eMins = extraMinutes % 60;
                  
                  updatedRecord.worked_hours = `${wHours}:${wMinutes.toString().padStart(2, '0')}`;
                  updatedRecord.extra_hours = `${eHours}:${eMins.toString().padStart(2, '0')}`;
                }
              }
            }
          });
        });
      });
      
      setGroupedData(updatedGroupedData);

      // Make API call to server
      console.log('Making API call with:', {
        employee_id: record.employee_id,
        date: record.date,
        field,
        value: field === 'comments' ? newValue : (field === 'check_in' || field === 'check_out' ? (newValue.includes(':') && newValue.split(':').length === 2 ? newValue + ':00' : newValue) : newValue),
        originalValue: record[field]
      });
      
      let response;
      if (field === 'date') {
        response = await attendanceAPI.updateAttendanceTime({
          employee_id: record.employee_id,
          date: record.date,
          field: 'date',
          value: newValue
        });
      } else {
        response = await attendanceAPI.updateAttendanceTime({
          employee_id: record.employee_id,
          date: record.date,
          field,
          value: field === 'comments' ? newValue : (field === 'check_in' || field === 'check_out' ? (newValue.includes(':') && newValue.split(':').length === 2 ? newValue + ':00' : newValue) : newValue)
        });
      }

      if (response.data.success) {
        // Update was successful, keep the optimistic update
        // No need to refresh immediately as the optimistic update is already applied
        console.log('Attendance updated successfully');
      } else {
        // If server update failed, revert the optimistic update
        setGroupedData(originalGroupedData);
        setError('Failed to update attendance time');
      }
    } catch (error) {
      console.error('Error updating attendance:', error);
      // Revert optimistic update on error
      setGroupedData(originalGroupedData);
      setError('Failed to update attendance time');
    } finally {
      setEditingCell(null);
      setEditingValue('');
    }
  };

  const handleAddRecord = async () => {
    try {
      if (!newRecord.employee_id || !newRecord.date) {
        setError('Employee and date are required');
        return;
      }

      const response = await attendanceAPI.createAttendanceRecord({
        employee_id: newRecord.employee_id,
        date: newRecord.date,
        check_in: newRecord.check_in ? newRecord.check_in + ':00' : '',
        check_out: newRecord.check_out ? newRecord.check_out + ':00' : ''
      });

      if (response.data.success) {
        setShowAddModal(false);
        setNewRecord({
          employee_id: '',
          date: format(new Date(), 'yyyy-MM-dd'),
          check_in: '',
          check_out: ''
        });
        refreshAttendanceData();
      }
    } catch (error) {
      console.error('Error creating attendance record:', error);
      setError('Failed to create attendance record');
    }
  };

  const toggleEmployeeCollapse = (employeeKey) => {
    const newCollapsed = new Set(collapsedEmployees);
    if (newCollapsed.has(employeeKey)) {
      newCollapsed.delete(employeeKey);
    } else {
      newCollapsed.add(employeeKey);
    }
    setCollapsedEmployees(newCollapsed);
  };

  const toggleMonthCollapse = (monthKey) => {
    const newCollapsed = new Set(collapsedMonths);
    if (newCollapsed.has(monthKey)) {
      newCollapsed.delete(monthKey);
    } else {
      newCollapsed.add(monthKey);
    }
    setCollapsedMonths(newCollapsed);
  };

  const toggleDateCollapse = (dateKey) => {
    const newCollapsed = new Set(collapsedDates);
    if (newCollapsed.has(dateKey)) {
      newCollapsed.delete(dateKey);
    } else {
      newCollapsed.add(dateKey);
    }
    setCollapsedDates(newCollapsed);
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-8 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const canEditTimes = (record) => {
    if (!userPermissions) return false;
    
    // Admin and HR Manager can edit all attendance times
    if (userPermissions.user.role === 'admin' || userPermissions.user.role === 'hr_manager') {
      return true;
    }
    
    // Department managers can edit their team members' times
    if (userPermissions.isDepartmentManager && userPermissions.managedDepartments) {
      // Need to check if the record's employee is in a department this user manages
      // For now, we'll rely on the backend to handle this validation
      return userPermissions.canEditTimes;
    }
    
    return false;
  };

  console.log('Permissions debug:', { userPermissions, canEditTimes: userPermissions?.canEditTimes });

  const canEditComments = (record) => {
    if (!userPermissions) return false;
    
    // Admin, HR Manager, and Department Managers can edit all comments
    if (userPermissions.user.role === 'admin' || 
        userPermissions.user.role === 'hr_manager' || 
        userPermissions.isDepartmentManager) {
      return true;
    }
    
    // Employees can edit comments on their own records
    return userPermissions.user && 
           userPermissions.user.employee_id && 
           record.employee_id === userPermissions.user.employee_id;
  };

  const canAdd = userPermissions && (
    userPermissions.user.role === 'admin' || 
    userPermissions.user.role === 'hr_manager' ||
    userPermissions.isDepartmentManager
  );

  return (
    <div className="p-4">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Attendance Management</h1>
            {userPermissions && (
              <p className="text-sm text-gray-500 mt-1">
                Access Level: <span className="font-medium capitalize">{userPermissions.accessLevel}</span>
                {userPermissions.user && ` | ${userPermissions.user.role}`}
                {userPermissions.isDepartmentManager && (
                  <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                    Dept Manager: {userPermissions.managedDepartments.map(d => d.name).join(', ')}
                  </span>
                )}
              </p>
            )}
          </div>
          {canAdd && (
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              Add Record
            </button>
          )}
        </div>
        
        {/* Search Control */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search by name or employee number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full max-w-md"
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-4">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {Object.entries(groupedData)
            .sort(([,a], [,b]) => a.info.name.localeCompare(b.info.name))
            .map(([employeeKey, employeeData]) => (
            <div key={employeeKey} className="border border-gray-200 rounded-lg">
              {/* Employee Header */}
              <div
                className="bg-blue-50 px-4 py-3 border-b border-gray-200 cursor-pointer flex items-center justify-between hover:bg-blue-100"
                onClick={() => toggleEmployeeCollapse(employeeKey)}
              >
                <div className="flex items-center gap-3">
                  {collapsedEmployees.has(employeeKey) ? (
                    <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                  )}
                  <UserIcon className="h-5 w-5 text-blue-500" />
                  <div>
                    <h2 className="text-lg font-semibold text-gray-800">{employeeData.info.name}</h2>
                    <p className="text-sm text-gray-500">
                      {employeeData.info.employee_number} | {employeeData.info.department} | {employeeData.info.job_title}
                    </p>
                  </div>
                </div>
                <span className="text-sm text-gray-500">
                  {Object.values(employeeData.months).reduce((sum, month) => 
                    sum + Object.values(month.dates).reduce((dateSum, date) => dateSum + date.records.length, 0), 0
                  )} records
                </span>
              </div>

              {/* Employee Content - Months */}
              {!collapsedEmployees.has(employeeKey) && (
                <div className="divide-y divide-gray-100">
                  {Object.entries(employeeData.months)
                    .sort(([a], [b]) => b.localeCompare(a))
                    .map(([monthKey, monthData]) => (
                    <div key={`${employeeKey}-${monthKey}`}>
                      {/* Month Header */}
                      <div
                        className="bg-gray-50 px-6 py-2 cursor-pointer flex items-center justify-between hover:bg-gray-100"
                        onClick={() => toggleMonthCollapse(`${employeeKey}-${monthKey}`)}
                      >
                        <div className="flex items-center gap-2">
                          {collapsedMonths.has(`${employeeKey}-${monthKey}`) ? (
                            <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                          ) : (
                            <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                          )}
                          <BriefcaseIcon className="h-4 w-4 text-gray-400" />
                          <span className="font-medium text-gray-700">{monthData.label}</span>
                        </div>
                        <span className="text-sm text-gray-500">
                          {Object.values(monthData.dates).reduce((sum, date) => sum + date.records.length, 0)} records
                        </span>
                      </div>

                      {/* Month Content - Dates */}
                      {!collapsedMonths.has(`${employeeKey}-${monthKey}`) && (
                        <div className="divide-y divide-gray-50">
                          {Object.entries(monthData.dates)
                            .sort(([a], [b]) => b.localeCompare(a))
                            .map(([dateKey, dateData]) => (
                            <div key={`${employeeKey}-${monthKey}-${dateKey}`}>
                              {/* Date Header */}
                              <div
                                className="bg-white px-8 py-2 cursor-pointer flex items-center justify-between hover:bg-gray-50"
                                onClick={() => toggleDateCollapse(`${employeeKey}-${monthKey}-${dateKey}`)}
                              >
                                <div className="flex items-center gap-2">
                                  {collapsedDates.has(`${employeeKey}-${monthKey}-${dateKey}`) ? (
                                    <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                                  ) : (
                                    <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                                  )}
                                  <CalendarIcon className="h-4 w-4 text-gray-400" />
                                  <span className="font-medium text-gray-700">{dateData.label}</span>
                                </div>
                                <span className="text-sm text-gray-500">{dateData.records.length} records</span>
                              </div>

                              {/* Date Records */}
                              {!collapsedDates.has(`${employeeKey}-${monthKey}-${dateKey}`) && (
                                <div className="bg-white ml-4">
                                  <table className="min-w-full">
                                    <thead className="bg-gray-50">
                                      <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check In</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check Out</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Worked Hours</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Extra Hours</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comments</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                      {dateData.records.map((record) => (
                                        <tr key={record.id} className="hover:bg-gray-50">
                                          <td className="px-6 py-4 text-sm text-gray-500">
                                            {format(parseISO(record.date), 'MMM dd')}
                                          </td>
                                          <td className="px-6 py-4 text-sm text-gray-500">
                                            <div className="flex items-center gap-2">
                                              {canEditTimes(record) && editingCell === `${record.id}-check_in` ? (
                                                <input
                                                  type="time"
                                                  value={editingValue}
                                                  onChange={(e) => setEditingValue(e.target.value)}
                                                  onBlur={() => handleCellEdit(record, 'check_in', editingValue)}
                                                  onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleCellEdit(record, 'check_in', editingValue);
                                                    if (e.key === 'Escape') { setEditingCell(null); setEditingValue(''); }
                                                  }}
                                                  className="px-2 py-1 border border-gray-300 rounded text-sm"
                                                  autoFocus
                                                />
                                              ) : (
                                                <>
                                                  <span>{record.check_in || '--:--'}</span>
                                                  {canEditTimes(record) && (
                                                    <PencilIcon
                                                      className="h-4 w-4 text-gray-400 hover:text-blue-500 cursor-pointer"
                                                      onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        startCellEdit(`${record.id}-check_in`, record.check_in);
                                                      }}
                                                    />
                                                  )}
                                                </>
                                              )}
                                            </div>
                                          </td>
                                          <td className="px-6 py-4 text-sm text-gray-500">
                                            <div className="flex items-center gap-2">
                                              {canEditTimes(record) && editingCell === `${record.id}-check_out` ? (
                                                <input
                                                  type="time"
                                                  value={editingValue}
                                                  onChange={(e) => setEditingValue(e.target.value)}
                                                  onBlur={() => handleCellEdit(record, 'check_out', editingValue)}
                                                  onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleCellEdit(record, 'check_out', editingValue);
                                                    if (e.key === 'Escape') { setEditingCell(null); setEditingValue(''); }
                                                  }}
                                                  className="px-2 py-1 border border-gray-300 rounded text-sm"
                                                  autoFocus
                                                />
                                              ) : (
                                                <>
                                                  <span>{record.check_out || '--:--'}</span>
                                                  {canEditTimes(record) && (
                                                    <PencilIcon
                                                      className="h-4 w-4 text-gray-400 hover:text-blue-500 cursor-pointer"
                                                      onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        startCellEdit(`${record.id}-check_out`, record.check_out);
                                                      }}
                                                    />
                                                  )}
                                                </>
                                              )}
                                            </div>
                                          </td>
                                          <td className="px-6 py-4 text-sm text-gray-500">{record.worked_hours}</td>
                                          <td className="px-6 py-4 text-sm text-gray-500">{record.extra_hours}</td>
                                          <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">
                                            <div className="flex items-center gap-2">
                                              {canEditComments(record) && editingCell === `${record.id}-comments` ? (
                                                <textarea
                                                  value={editingValue}
                                                  onChange={(e) => setEditingValue(e.target.value)}
                                                  onBlur={() => handleCellEdit(record, 'comments', editingValue)}
                                                  onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                      e.preventDefault();
                                                      handleCellEdit(record, 'comments', editingValue);
                                                    }
                                                    if (e.key === 'Escape') { setEditingCell(null); setEditingValue(''); }
                                                  }}
                                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm min-h-[60px] resize-y"
                                                  placeholder="Add comments..."
                                                  autoFocus
                                                />
                                              ) : (
                                                <>
                                                  <span className="break-words" title={record.comments}>
                                                    {record.comments || 'No comments'}
                                                  </span>
                                                  {canEditComments(record) && (
                                                    <PencilIcon
                                                      className="h-4 w-4 text-gray-400 hover:text-blue-500 cursor-pointer flex-shrink-0"
                                                      onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        startCellEdit(`${record.id}-comments`, record.comments);
                                                      }}
                                                    />
                                                  )}
                                                </>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add Record Modal */}
        {showAddModal && canAdd && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Add Attendance Record</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
                  <select
                    value={newRecord.employee_id}
                    onChange={(e) => setNewRecord({ ...newRecord, employee_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Employee</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name} - {emp.employee_number}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={newRecord.date}
                    onChange={(e) => setNewRecord({ ...newRecord, date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check In</label>
                  <input
                    type="time"
                    value={newRecord.check_in}
                    onChange={(e) => setNewRecord({ ...newRecord, check_in: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check Out</label>
                  <input
                    type="time"
                    value={newRecord.check_out}
                    onChange={(e) => setNewRecord({ ...newRecord, check_out: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddRecord}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  Add Record
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceManagement;